import React, { useState, useEffect, useCallback } from 'react';
import { 
  Plus, Edit2, Trash2, FileText, X, Save, Loader2, Search,
  Power, PowerOff, MessageSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status Badge
const StatusBadge = ({ status }) => {
  const isActive = status === 'Active';
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
    }`}>
      {status}
    </span>
  );
};

// Template Card
const TemplateCard = ({ template, onEdit, onDelete, onToggle }) => (
  <div 
    className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-all"
    data-testid={`template-card-${template.id}`}
  >
    <div className="flex items-start justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
          <FileText size={20} className="text-indigo-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-800">{template.name}</h3>
          <StatusBadge status={template.status} />
        </div>
      </div>
      <button
        onClick={() => onToggle(template)}
        className={`p-2 rounded-lg transition-colors ${
          template.status === 'Active' 
            ? 'text-green-600 hover:bg-green-50' 
            : 'text-gray-400 hover:bg-gray-100'
        }`}
      >
        {template.status === 'Active' ? <Power size={18} /> : <PowerOff size={18} />}
      </button>
    </div>
    
    <p className="text-sm text-gray-600 mb-4 line-clamp-3 bg-gray-50 p-3 rounded-lg">
      {template.message}
    </p>
    
    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
      <span className="text-xs text-gray-500">
        {template.createdAt && new Date(template.createdAt).toLocaleDateString()}
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onEdit(template)}
          className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => onDelete(template)}
          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  </div>
);

// Template Modal
const TemplateModal = ({ isOpen, onClose, onSave, template, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    message: '',
    status: 'Active'
  });
  
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        message: template.message || '',
        status: template.status || 'Active'
      });
    } else {
      setFormData({ name: '', message: '', status: 'Active' });
    }
  }, [template, isOpen]);
  
  if (!isOpen) return null;
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.message.trim()) return;
    onSave(formData);
  };
  
  const insertVariable = (v) => {
    setFormData(prev => ({ ...prev, message: prev.message + `{${v}}` }));
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            {template ? 'Edit Template' : 'Create Template'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} className="text-gray-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Template Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Welcome Message, Follow Up"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
              required
              data-testid="template-name-input"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
            <div className="mb-2 flex flex-wrap gap-1">
              <span className="text-xs text-gray-500">Insert variable:</span>
              {['hotelName', 'ownerName', 'phone', 'city', 'email'].map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  className="px-2 py-0.5 text-xs bg-indigo-50 text-indigo-700 rounded hover:bg-indigo-100"
                >
                  {`{${v}}`}
                </button>
              ))}
            </div>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="Type the template message..."
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none resize-none"
              required
              data-testid="template-message-input"
            />
            <p className="mt-1 text-xs text-gray-500">{formData.message.length} characters</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, status: 'Active' }))}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  formData.status === 'Active'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Power size={14} className="inline mr-2" />
                Active
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, status: 'Inactive' }))}
                className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                  formData.status === 'Inactive'
                    ? 'border-gray-500 bg-gray-50 text-gray-700'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <PowerOff size={14} className="inline mr-2" />
                Inactive
              </button>
            </div>
          </div>
        </form>
        
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !formData.name.trim() || !formData.message.trim()}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50"
            data-testid="save-template-btn"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {template ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Automation Templates Page
const AutomationTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/automation/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setTemplates(data.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);
  
  const handleSaveTemplate = async (formData) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = selectedTemplate 
        ? `${API_URL}/api/automation/templates/${selectedTemplate.id}`
        : `${API_URL}/api/automation/templates`;
      
      const response = await fetch(url, {
        method: selectedTemplate ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) throw new Error('Failed to save template');
      
      await fetchTemplates();
      setModalOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/automation/templates/${selectedTemplate.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchTemplates();
      setDeleteModalOpen(false);
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Error deleting template:', error);
    } finally {
      setSaving(false);
    }
  };
  
  const handleToggleTemplate = async (template) => {
    try {
      const token = localStorage.getItem('token');
      const newStatus = template.status === 'Active' ? 'Inactive' : 'Active';
      await fetch(`${API_URL}/api/automation/templates/${template.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      await fetchTemplates();
    } catch (error) {
      console.error('Error toggling template:', error);
    }
  };
  
  const filteredTemplates = templates.filter(t => 
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  return (
    <div className="space-y-6" data-testid="automation-templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Automation Templates</h1>
          <p className="text-gray-500 mt-1">Reusable message templates for automations and chatbots</p>
        </div>
        <button
          onClick={() => {
            setSelectedTemplate(null);
            setModalOpen(true);
          }}
          className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium"
          data-testid="create-template-btn"
        >
          <Plus size={18} />
          Create Template
        </button>
      </div>
      
      {/* Search */}
      <div className="relative">
        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search templates..."
          className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none"
        />
      </div>
      
      {/* Templates Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={32} className="animate-spin text-indigo-600" />
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-2">No Templates Yet</h3>
          <p className="text-gray-500 mb-4">Create your first template for quick automation responses</p>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setModalOpen(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Plus size={18} />
            Create Template
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={(t) => {
                setSelectedTemplate(t);
                setModalOpen(true);
              }}
              onDelete={(t) => {
                setSelectedTemplate(t);
                setDeleteModalOpen(true);
              }}
              onToggle={handleToggleTemplate}
            />
          ))}
        </div>
      )}
      
      {/* Template Modal */}
      <TemplateModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedTemplate(null);
        }}
        onSave={handleSaveTemplate}
        template={selectedTemplate}
        loading={saving}
      />
      
      {/* Delete Confirmation */}
      {deleteModalOpen && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trash2 size={32} className="text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Delete Template</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <strong>"{selectedTemplate.name}"</strong>?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => {
                  setDeleteModalOpen(false);
                  setSelectedTemplate(null);
                }}
                className="px-6 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTemplate}
                disabled={saving}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutomationTemplates;
